import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import CaptureImage from "../../../components/common/CaptureImage";
import {
  RgbConfig,
  RgbMovementType,
  RgbPackaging,
  RgbRefugoMotivo,
  RgbStore,
  createRgbMovementApi,
} from "../api/rgbApi";
import { todayIso } from "../rgbFormat";
import RgbPhoto from "./RgbPhoto";
import { useModalChrome } from "./useModalChrome";

const MAX_PHOTOS = 10;

type Props = {
  isOpen: boolean;
  token: string;
  config: RgbConfig;
  store: RgbStore | null;
  packagings: RgbPackaging[];
  onClose: () => void;
  onCreated: () => void;
};

/**
 * Apontamento manual. As regras são do backend (a mensagem de erro dele é a
 * que aparece); aqui o formulário só esconde o que não se aplica: motivo e foto
 * existem só no refugo, observação é obrigatória no ajuste.
 */
export default function RgbMovementModal({ isOpen, token, config, store, packagings, onClose, onCreated }: Props) {
  const [type, setType] = useState<RgbMovementType>("REFUGO");
  const [packagingId, setPackagingId] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [data, setData] = useState(todayIso());
  const [motivo, setMotivo] = useState<RgbRefugoMotivo | "">("");
  const [observacao, setObservacao] = useState("");
  const [fotos, setFotos] = useState<string[]>([]);
  const [captureKey, setCaptureKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useModalChrome(isOpen, onClose, saving);

  useEffect(() => {
    if (!isOpen) return;
    setType(config.tipos.some((t) => t.value === "REFUGO") ? "REFUGO" : config.tipos[0]?.value ?? "REFUGO");
    setPackagingId(packagings.length === 1 ? packagings[0].id : "");
    setQuantidade("");
    setData(todayIso());
    setMotivo("");
    setObservacao("");
    setFotos([]);
    setCaptureKey((k) => k + 1);
  }, [isOpen, config.tipos, packagings]);

  const isRefugo = type === "REFUGO";
  const isAjuste = type === "AJUSTE_SALDO";
  const qtd = Number(quantidade);

  const problem = useMemo(() => {
    if (!packagingId) return "Escolha o vasilhame.";
    if (quantidade.trim() === "" || !Number.isInteger(qtd)) return "Informe a quantidade (número inteiro).";
    if (isAjuste ? qtd === 0 : qtd <= 0) return isAjuste ? "Ajuste não pode ser zero." : "Quantidade deve ser maior que zero.";
    if (!data) return "Informe a data.";
    if (data > todayIso()) return "A data não pode ser futura.";
    if (isRefugo && !motivo) return "Escolha o motivo do refugo.";
    if (isAjuste && !observacao.trim()) return "Descreva o motivo do ajuste na observação.";
    if (isRefugo && config.fotoObrigatoria && fotos.length === 0) return "Anexe ao menos uma foto.";
    return null;
  }, [packagingId, quantidade, qtd, isAjuste, isRefugo, data, motivo, observacao, config.fotoObrigatoria, fotos.length]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store || problem) return;

    setSaving(true);
    try {
      await createRgbMovementApi({
        storeId: store.id,
        packagingId,
        type,
        quantidade: qtd,
        data,
        motivo: isRefugo ? (motivo as RgbRefugoMotivo) : null,
        observacao: observacao.trim() || null,
        fotoAssetIds: isRefugo ? fotos : [],
      });
      toast.success("Apontamento registrado.");
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao registrar o apontamento.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !store) return null;

  return (
    <div className="rgbModalOverlay" onClick={() => !saving && onClose()}>
      <div
        className="rgbModal"
        role="dialog"
        aria-modal="true"
        aria-label="Novo apontamento de vasilhame"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="rgbModal__header">
            <h2 className="rgbModal__title">Novo apontamento</h2>
            <button type="button" className="rgbModal__close" onClick={onClose} disabled={saving} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="rgbModal__body">
            <div className="rgbNotice" style={{ marginBottom: 0 }}>
              Loja: <strong>{store.nomeFantasia}</strong>
            </div>

            <div className="rgbFormGrid">
              {config.tipos.length > 1 ? (
                <div className="rgbFormGroup">
                  <label htmlFor="rgbType">Tipo</label>
                  <select
                    id="rgbType"
                    className="rgbSelect"
                    value={type}
                    onChange={(e) => setType(e.target.value as RgbMovementType)}
                  >
                    {config.tipos.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="rgbFormGroup">
                <label htmlFor="rgbPackaging">Vasilhame</label>
                <select
                  id="rgbPackaging"
                  className="rgbSelect"
                  value={packagingId}
                  onChange={(e) => setPackagingId(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {packagings.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rgbFormGroup">
                <label htmlFor="rgbQtd">Quantidade</label>
                <input
                  id="rgbQtd"
                  className="rgbInput"
                  type="number"
                  inputMode="numeric"
                  step={1}
                  min={isAjuste ? undefined : 1}
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                />
                {isAjuste ? <span className="rgbFormHint">Negativo diminui o saldo.</span> : null}
              </div>

              <div className="rgbFormGroup">
                <label htmlFor="rgbData">Data</label>
                <input
                  id="rgbData"
                  className="rgbInput"
                  type="date"
                  max={todayIso()}
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              </div>

              {isRefugo ? (
                <div className="rgbFormGroup">
                  <label htmlFor="rgbMotivo">Motivo</label>
                  <select
                    id="rgbMotivo"
                    className="rgbSelect"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value as RgbRefugoMotivo)}
                  >
                    <option value="">Selecione...</option>
                    {config.motivos.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="rgbFormGroup rgbFormGrid--wide">
                <label htmlFor="rgbObs">Observação{isAjuste ? " (obrigatória)" : ""}</label>
                <textarea
                  id="rgbObs"
                  className="rgbTextarea"
                  maxLength={500}
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                />
              </div>
            </div>

            {isRefugo ? (
              <div className="rgbFormGroup">
                <label>
                  Fotos{config.fotoObrigatoria ? " (obrigatória ao menos uma)" : " (opcional)"}
                </label>
                {fotos.length > 0 ? (
                  <div className="rgbThumbs">
                    {fotos.map((assetId, i) => (
                      <div key={assetId} className="rgbThumb">
                        <RgbPhoto token={token} assetId={assetId} alt={`Foto ${i + 1}`} />
                        <button
                          type="button"
                          className="rgbThumb__remove"
                          aria-label={`Remover foto ${i + 1}`}
                          onClick={() => setFotos((prev) => prev.filter((id) => id !== assetId))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {fotos.length < MAX_PHOTOS ? (
                  <CaptureImage
                    // Nova key a cada foto enviada: o componente volta ao estado
                    // inicial e permite tirar a próxima.
                    key={captureKey}
                    token={token}
                    label={fotos.length ? "Adicionar outra foto" : "Foto do vasilhame refugado"}
                    onUploadSuccess={(assetId) => {
                      setFotos((prev) => (prev.includes(assetId) ? prev : [...prev, assetId]));
                      setCaptureKey((k) => k + 1);
                    }}
                  />
                ) : (
                  <span className="rgbFormHint">Limite de {MAX_PHOTOS} fotos atingido.</span>
                )}
              </div>
            ) : null}

            {problem ? <span className="rgbFormHint">{problem}</span> : null}
          </div>

          <div className="rgbModal__footer">
            <button type="button" className="rgbBtn rgbBtn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="rgbBtn rgbBtn--primary" disabled={saving || Boolean(problem)}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
